<template>
  <div class="dh-avatar" :class="avatarClass" :style="avatarStyle">
    <!-- 图片头像 -->
    <img
      v-if="src && !imageError"
      :src="src"
      :alt="alt"
      class="avatar-image"
      @error="handleImageError"
      @load="handleImageLoad"
    />

    <!-- 图标头像 -->
    <ElIcon v-else-if="icon" :size="iconSize" class="avatar-icon">
      <component :is="icon" />
    </ElIcon>

    <!-- 文字头像 -->
    <span v-else class="avatar-text" :style="textStyle">
      {{ displayText }}
    </span>

    <!-- 加载状态 -->
    <div v-if="loading" class="avatar-loading">
      <ElIcon class="loading-icon">
        <Loading />
      </ElIcon>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 头像组件
 * 支持图片、图标、文字三种类型的头像显示
 * @author 系统
 * @date 2024-01-01
 */

import { ref, computed, watch } from 'vue'
import { Loading } from '@element-plus/icons-vue'
import type { Component } from 'vue'

interface Props {
  /** 头像图片地址 */
  src?: string
  /** 图片描述 */
  alt?: string
  /** 头像大小 */
  size?: number | 'small' | 'default' | 'large'
  /** 头像形状 */
  shape?: 'circle' | 'square'
  /** 图标 */
  icon?: Component
  /** 文字内容 */
  text?: string
  /** 背景色 */
  backgroundColor?: string
  /** 文字颜色 */
  textColor?: string
  /** 是否可点击 */
  clickable?: boolean
}

interface Emits {
  /** 点击事件 */
  click: [event: MouseEvent]
  /** 图片加载错误事件 */
  error: [event: Event]
  /** 图片加载成功事件 */
  load: [event: Event]
}

const props = withDefaults(defineProps<Props>(), {
  size: 'default',
  shape: 'circle',
  alt: '头像',
  clickable: false
})

const emit = defineEmits<Emits>()

// 响应式数据
const imageError = ref(false)
const loading = ref(false)

// 计算属性
const avatarClass = computed(() => {
  return [
    `avatar-${props.shape}`,
    `avatar-${typeof props.size === 'string' ? props.size : 'custom'}`,
    {
      'avatar-clickable': props.clickable
    }
  ]
})

const avatarStyle = computed(() => {
  const style: Record<string, any> = {}

  if (typeof props.size === 'number') {
    style.width = `${props.size}px`
    style.height = `${props.size}px`
  }

  if (props.backgroundColor) {
    style.backgroundColor = props.backgroundColor
  }

  return style
})

const iconSize = computed(() => {
  if (typeof props.size === 'number') {
    return Math.floor(props.size * 0.6)
  }

  const sizeMap = {
    small: 14,
    default: 18,
    large: 24
  }

  return sizeMap[props.size as keyof typeof sizeMap] || 18
})

const displayText = computed(() => {
  if (!props.text) return ''

  // 如果是中文，取前两个字符
  if (/[\u4e00-\u9fa5]/.test(props.text)) {
    return props.text.slice(0, 2)
  }

  // 如果是英文，取首字母
  const words = props.text.split(' ').filter(word => word.length > 0)
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  return props.text.slice(0, 2).toUpperCase()
})

const textStyle = computed(() => {
  const style: Record<string, any> = {}

  if (props.textColor) {
    style.color = props.textColor
  }

  if (typeof props.size === 'number') {
    style.fontSize = `${Math.floor(props.size * 0.4)}px`
  }

  return style
})

// 方法
const handleImageError = (event: Event) => {
  imageError.value = true
  loading.value = false
  emit('error', event)
}

const handleImageLoad = (event: Event) => {
  imageError.value = false
  loading.value = false
  emit('load', event)
}

const handleClick = (event: MouseEvent) => {
  if (props.clickable) {
    emit('click', event)
  }
}

// 监听 src 变化
watch(
  () => props.src,
  (newSrc) => {
    if (newSrc) {
      imageError.value = false
      loading.value = true
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.dh-avatar {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: var(--el-fill-color-light);
  color: var(--el-text-color-regular);
  font-weight: 500;
  user-select: none;
  transition: all 0.3s;
}

/* 形状 */
.avatar-circle {
  border-radius: 50%;
}

.avatar-square {
  border-radius: 6px;
}

/* 大小 */
.avatar-small {
  width: 24px;
  height: 24px;
  font-size: 12px;
}

.avatar-default {
  width: 32px;
  height: 32px;
  font-size: 14px;
}

.avatar-large {
  width: 40px;
  height: 40px;
  font-size: 16px;
}

/* 可点击 */
.avatar-clickable {
  cursor: pointer;
}

.avatar-clickable:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

/* 图片 */
.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 图标 */
.avatar-icon {
  color: inherit;
}

/* 文字 */
.avatar-text {
  line-height: 1;
  font-weight: 600;
}

/* 加载状态 */
.avatar-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
}

.loading-icon {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
